/**
 * Customer Portal API Client
 * 
 * TypeScript client for customer portal endpoints.
 */

// In dev, we strongly prefer local API to avoid accidentally pointing the local portal UI at production
// due to a shared .env used for builds/deploys.
const DEV_FALLBACK_API_URL = 'http://localhost:3004';
const PROD_FALLBACK_API_URL = 'https://marketing.rightawayhome.com';

const rawEnvApiUrl = (import.meta.env.VITE_API_URL || '').trim();

const API_BASE_URL = import.meta.env.DEV
  ? rawEnvApiUrl && /localhost|127\.0\.0\.1/i.test(rawEnvApiUrl)
    ? rawEnvApiUrl
    : DEV_FALLBACK_API_URL
  : rawEnvApiUrl || PROD_FALLBACK_API_URL;

export interface PortalLoginRequest {
  email: string;
  phone: string;
  company_id: string;
}

export interface CancellationQuote {
  billing_frequency: 'monthly' | 'annual';
  commitment_payments: number | null;
  payments_made: number;
  remaining_payments: number;
  payoff_amount_cents: number;
  can_cancel_now: boolean;
}

export interface PortalLoginResponse {
  success: boolean;
  membership_id: string;
  portal_user_id: string;
  membership: Membership;
}

export interface Membership {
  id: string;
  company_id: string;
  plan_id: string;
  plan_code: string;
  plan_name: string;

  // Enriched plan details (from membership_plans)
  plan_description?: string | null;
  plan_features?: string[];
  plan_coverage_details?: any;
  
  status: 'active' | 'cancelled' | 'suspended' | 'expired';
  
  billing_frequency: 'monthly' | 'annual';
  tune_ups_per_year: number;
  systems_count: number;
  total_visits_allowed: number;
  visits_used: number;
  
  base_price_cents: number;
  discount_code_id?: string;
  discount_amount_cents: number;
  final_price_cents: number;
  
  start_date: string;
  next_billing_date: string;
  cancellation_date?: string;
  cancellation_reason?: string;
  
  stax_customer_id: string;
  stax_payment_method_id: string;
  stax_subscription_id: string;
  
  customer_email: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_phone: string;
  customer_address: string;
  customer_city: string;
  customer_state: string;
  customer_zip: string;
  
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export interface Transaction {
  id: string;
  membership_id: string;
  company_id: string;
  
  transaction_type: 'payment' | 'refund' | 'adjustment' | 'failed_payment';
  amount_cents: number;
  status: 'pending' | 'completed' | 'succeeded' | 'failed' | 'refunded';
  
  stax_transaction_id?: string;
  stax_customer_id?: string;
  stax_payment_method_id?: string;

  // Optional enriched fields (present in membership_transactions rows)
  description?: string;
  payment_method?: string;
  card_last_four?: string | null;
  card_brand?: string | null;
  
  billing_period_start?: string;
  billing_period_end?: string;
  
  metadata?: any;
  created_at: string;
}

export interface Visit {
  id: string;
  membership_id: string;
  company_id: string;
  
  visit_date: string;
  visit_window?: string;
  visit_type: 'pm' | 'repair' | 'emergency' | 'other';
  
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  
  technician_id?: string;
  technician_name?: string;

  job_id?: string;
  job_number?: string;
  
  // Backwards-compatible aliases (API still returns these)
  successware_job_id?: string;
  successware_job_number?: string;
  
  notes?: string;
  metadata?: any;
  created_at: string;
}

export interface AvailableSlot {
  date: string;
  window: string;
  technicianId?: string;
  available: boolean;
}

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    // Some errors come back as non-JSON (e.g., upstream/proxy issues). Be tolerant.
    const rawText = await response.text();
    let data: any = null;
    try {
      data = rawText ? JSON.parse(rawText) : null;
    } catch {
      data = null;
    }

    if (!response.ok) {
      throw new Error(data?.message || data?.error || `API request failed (${response.status})`);
    }

    return data as T;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

export async function portalLogin(request: PortalLoginRequest): Promise<PortalLoginResponse> {
  return apiRequest<PortalLoginResponse>('/api/portal/login', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function getMembership(membershipId: string): Promise<{ membership: Membership }> {
  return apiRequest<{ membership: Membership }>(`/api/portal/membership/${membershipId}`);
}

export async function getTransactions(membershipId: string): Promise<{ transactions: Transaction[] }> {
  return apiRequest<{ transactions: Transaction[] }>(`/api/portal/membership/${membershipId}/transactions`);
}

export async function getCancellationQuote(membershipId: string): Promise<{ quote: CancellationQuote }> {
  return apiRequest<{ quote: CancellationQuote }>(
    `/api/portal/membership/${membershipId}/cancellation-quote`
  );
}

export async function getVisits(membershipId: string): Promise<{
  visits: Visit[];
  past: Visit[];
  upcoming: Visit[];
}> {
  return apiRequest(`/api/portal/membership/${membershipId}/visits`);
}

export async function updatePaymentMethod(
  membershipId: string,
  paymentToken: string,
  paymentMethodType: 'card' | 'bank'
): Promise<{
  success: boolean;
  membership: Membership;
}> {
  return apiRequest(`/api/portal/membership/${membershipId}/update-payment`, {
    method: 'POST',
    body: JSON.stringify({
      payment_token: paymentToken,
      payment_method_type: paymentMethodType,
    }),
  });
}

export async function updateProfile(
  membershipId: string,
  updates: Partial<
    Pick<
      Membership,
      | 'customer_first_name'
      | 'customer_last_name'
      | 'customer_email'
      | 'customer_phone'
      | 'customer_address'
      | 'customer_city'
      | 'customer_state'
      | 'customer_zip'
    >
  >
): Promise<{
  success: boolean;
  membership: Membership;
}> {
  return apiRequest(`/api/portal/membership/${membershipId}/update-profile`, {
    method: 'POST',
    body: JSON.stringify(updates),
  });
}

export async function requestCancellation(
  membershipId: string,
  request: {
    reason?: string;
    requested_action?: 'cancel_at_end' | 'payoff';
  }
): Promise<{
  success: boolean;
  membership: Membership;
  cancellation_request: any;
}> {
  return apiRequest(`/api/portal/membership/${membershipId}/request-cancellation`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function getAvailableSlots(membershipId: string, days: number = 30): Promise<{
  availableSlots: AvailableSlot[];
}> {
  return apiRequest(`/api/portal/membership/${membershipId}/available-slots?days=${days}`);
}

export async function bookVisit(
  membershipId: string,
  visitData: {
    visit_date: string;
    visit_window: string;
    visit_type: 'pm' | 'repair' | 'emergency';
    notes?: string;
  }
): Promise<{
  success: boolean;
  visit: Visit;
  job_number: string;
}> {
  return apiRequest(`/api/portal/membership/${membershipId}/book-visit`, {
    method: 'POST',
    body: JSON.stringify(visitData),
  });
}

export async function cancelMembership(membershipId: string, reason?: string): Promise<{
  success: boolean;
  membership: Membership;
}> {
  return apiRequest(`/api/portal/membership/${membershipId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}
